import { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import { BookOpen, ChevronLeft, ChevronRight, Maximize2, Minimize2, ShoppingCart, Loader, ZoomIn, ZoomOut, RotateCcw } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { Link } from "react-router-dom";

const API = `${process.env.REACT_APP_BACKEND_URL || ""}/api`;

function BookReader({ token }) {
  const [pdfDoc, setPdfDoc] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [loading, setLoading] = useState(true);
  const [rendering, setRendering] = useState(false);
  const [flipClass, setFlipClass] = useState("");
  const [scale, setScale] = useState(1.5);
  const [fullscreen, setFullscreen] = useState(false);
  const canvasRef = useRef(null);
  const containerRef = useRef(null);
  const touchStartX = useRef(null);

  // Load PDF
  useEffect(() => {
    const load = async () => {
      try {
        const pdfjsLib = await import("pdfjs-dist");
        pdfjsLib.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js`;

        // Fetch PDF as blob through backend (auth-protected)
        const res = await fetch(`${API}/book/pdf`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (!res.ok) throw new Error("Failed to fetch PDF");
        const blob = await res.blob();
        const arrayBuffer = await blob.arrayBuffer();

        const doc = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
        setPdfDoc(doc);
        setTotalPages(doc.numPages);
        setLoading(false);
      } catch (err) {
        console.error("PDF load error:", err);
        setLoading(false);
      }
    };
    load();
  }, [token]);

  // Render page
  const renderPage = useCallback(async (pageNum) => {
    if (!pdfDoc || !canvasRef.current) return;
    setRendering(true);
    try {
      const page = await pdfDoc.getPage(pageNum);
      const viewport = page.getViewport({ scale });
      const canvas = canvasRef.current;
      const ctx = canvas.getContext("2d");
      canvas.height = viewport.height;
      canvas.width = viewport.width;
      await page.render({ canvasContext: ctx, viewport }).promise;
    } catch (err) {
      console.error("Render error:", err);
    }
    setRendering(false);
  }, [pdfDoc, scale]);

  useEffect(() => {
    if (pdfDoc) renderPage(currentPage);
  }, [pdfDoc, currentPage, scale, renderPage]);

  const goToPage = (dir) => {
    if (rendering) return;
    const next = currentPage + dir;
    if (next < 1 || next > totalPages) return;
    const cls = dir > 0 ? "page-flip-forward" : "page-flip-backward";
    setFlipClass(cls);
    setTimeout(() => {
      setCurrentPage(next);
      setFlipClass("");
    }, 275);
  };

  // Touch swipe
  const handleTouchStart = (e) => { touchStartX.current = e.touches[0].clientX; };
  const handleTouchEnd = (e) => {
    if (touchStartX.current === null) return;
    const diff = touchStartX.current - e.changedTouches[0].clientX;
    if (Math.abs(diff) > 50) goToPage(diff > 0 ? 1 : -1);
    touchStartX.current = null;
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center h-96 gap-4">
        <Loader className="w-12 h-12 text-[#2F5233] animate-spin" />
        <p className="text-[#5A5A5A] font-medium">Loading your book...</p>
      </div>
    );
  }

  if (!pdfDoc) {
    return (
      <div className="text-center p-8">
        <p className="text-red-500">Could not load the book. Please try refreshing.</p>
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      className={`flex flex-col items-center ${fullscreen ? "fixed inset-0 z-50 bg-[#1A1A1A] p-4" : ""}`}
      data-testid="book-reader"
    >
      {/* Controls Top */}
      <div className={`flex items-center justify-between w-full max-w-3xl mb-4 ${fullscreen ? "text-white" : ""}`}>
        <div className="flex items-center gap-2">
          <button onClick={() => setScale(s => Math.max(0.8, s - 0.25))} className={`p-2 rounded-full hover:bg-[#E6E2D3] transition-colors ${fullscreen ? "hover:bg-white/20" : ""}`}>
            <ZoomOut className="w-4 h-4" />
          </button>
          <span className="text-sm font-medium">{Math.round(scale * 100)}%</span>
          <button onClick={() => setScale(s => Math.min(3, s + 0.25))} className={`p-2 rounded-full hover:bg-[#E6E2D3] transition-colors ${fullscreen ? "hover:bg-white/20" : ""}`}>
            <ZoomIn className="w-4 h-4" />
          </button>
          <button onClick={() => setScale(1.5)} className={`p-2 rounded-full hover:bg-[#E6E2D3] transition-colors ${fullscreen ? "hover:bg-white/20" : ""}`}>
            <RotateCcw className="w-4 h-4" />
          </button>
        </div>
        <div className={`text-sm font-medium ${fullscreen ? "text-white" : "text-[#5A5A5A]"}`}>
          Page {currentPage} of {totalPages}
        </div>
        <button onClick={() => setFullscreen(!fullscreen)} className={`p-2 rounded-full hover:bg-[#E6E2D3] transition-colors ${fullscreen ? "hover:bg-white/20" : ""}`}>
          {fullscreen ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
        </button>
      </div>

      {/* Canvas */}
      <div
        className="relative flex items-center gap-4"
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
      >
        <button
          onClick={() => goToPage(-1)}
          disabled={currentPage === 1}
          className="p-3 bg-white border border-[#E6E2D3] rounded-full shadow hover:bg-[#E6E2D3] transition-all disabled:opacity-30 flex-shrink-0"
          data-testid="prev-page-btn"
        >
          <ChevronLeft className="w-6 h-6 text-[#2F5233]" />
        </button>

        <div className={`shadow-2xl rounded-lg overflow-hidden ${flipClass}`} style={{ perspective: "2000px" }}>
          <canvas
            ref={canvasRef}
            className="max-w-full block"
            style={{ maxHeight: fullscreen ? "calc(100vh - 160px)" : "75vh", width: "auto" }}
          />
          {rendering && (
            <div className="absolute inset-0 flex items-center justify-center bg-white/50">
              <Loader className="w-8 h-8 text-[#2F5233] animate-spin" />
            </div>
          )}
        </div>

        <button
          onClick={() => goToPage(1)}
          disabled={currentPage === totalPages}
          className="p-3 bg-white border border-[#E6E2D3] rounded-full shadow hover:bg-[#E6E2D3] transition-all disabled:opacity-30 flex-shrink-0"
          data-testid="next-page-btn"
        >
          <ChevronRight className="w-6 h-6 text-[#2F5233]" />
        </button>
      </div>

      {/* Progress bar */}
      <div className={`mt-6 w-full max-w-3xl px-4`}>
        <div className={`h-1.5 rounded-full ${fullscreen ? "bg-white/20" : "bg-[#E6E2D3]"}`}>
          <div
            className="h-full bg-[#2F5233] rounded-full transition-all duration-300"
            style={{ width: `${(currentPage / totalPages) * 100}%` }}
          />
        </div>
        <div className={`flex justify-between text-xs mt-1 ${fullscreen ? "text-white/50" : "text-[#5A5A5A]"}`}>
          <span>Beginning</span>
          <span>End</span>
        </div>
      </div>

      {/* Keyboard hint */}
      <p className={`mt-4 text-xs ${fullscreen ? "text-white/40" : "text-[#5A5A5A]"} text-center`}>
        Swipe left/right on touch screens. Use arrows to navigate.
      </p>
    </div>
  );
}

export default function MyLibrary() {
  const { user, getToken } = useAuth();
  const [hasAccess, setHasAccess] = useState(null);
  const [loading, setLoading] = useState(true);
  const [orders, setOrders] = useState([]);

  useEffect(() => {
    const token = getToken();
    Promise.all([
      axios.get(`${API}/book/access`, { headers: { Authorization: `Bearer ${token}` } }),
      axios.get(`${API}/orders/mine`, { headers: { Authorization: `Bearer ${token}` } })
    ]).then(([accessRes, ordersRes]) => {
      setHasAccess(accessRes.data.has_access);
      setOrders(ordersRes.data);
    }).catch(() => {
      setHasAccess(false);
    }).finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: "#FDFBF7" }}>
        <Loader className="w-12 h-12 text-[#2F5233] animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen py-12" style={{ backgroundColor: "#FDFBF7" }} data-testid="my-library-page">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-[#1A1A1A]" style={{ fontFamily: "'Playfair Display', serif" }}>
            My Library
          </h1>
          <p className="text-[#5A5A5A] mt-2">Welcome back, {user?.name}!</p>
        </div>

        {hasAccess ? (
          <div className="space-y-6">
            <div className="bg-white rounded-3xl border border-[#E6E2D3] p-6">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 bg-[#B2D3C2] rounded-full flex items-center justify-center">
                  <BookOpen className="w-5 h-5 text-[#2F5233]" />
                </div>
                <div>
                  <h2 className="font-bold text-[#1A1A1A]" style={{ fontFamily: "'Playfair Display', serif" }}>Expect to Win — Kids Edition</h2>
                  <p className="text-xs text-[#5A5A5A]">by Kyle Keith · Interactive Reader</p>
                </div>
              </div>
              <BookReader token={getToken()} />
            </div>
          </div>
        ) : (
          <div className="bg-white rounded-3xl border border-[#E6E2D3] p-12 text-center">
            <BookOpen className="w-16 h-16 text-[#E6E2D3] mx-auto mb-6" />
            <h2 className="text-2xl font-bold text-[#1A1A1A] mb-3" style={{ fontFamily: "'Playfair Display', serif" }}>
              No Digital Books Yet
            </h2>
            <p className="text-[#5A5A5A] mb-8 max-w-md mx-auto">
              Purchase the digital edition for just $5.95 to unlock instant access to the interactive reader — perfect for iPad and all devices.
            </p>
            <Link to="/shop" className="inline-flex items-center gap-2 bg-[#2F5233] text-white px-8 py-4 rounded-full font-bold hover:bg-[#1E3A21] transition-all hover:scale-105" data-testid="get-digital-book-btn">
              <ShoppingCart className="w-5 h-5" /> Get Digital Book — $5.95
            </Link>
          </div>
        )}

        {/* Order History */}
        {orders.length > 0 && (
          <div className="mt-8">
            <h2 className="text-2xl font-bold text-[#1A1A1A] mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
              Order History
            </h2>
            <div className="space-y-3">
              {orders.map(order => (
                <div key={order.id || order._id} className="bg-white rounded-2xl border border-[#E6E2D3] p-4 flex items-center justify-between" data-testid={`order-${order.order_number}`}>
                  <div>
                    <p className="font-bold text-[#1A1A1A]">{order.order_number}</p>
                    <p className="text-sm text-[#5A5A5A]">{order.items?.length} item(s) · ${order.total?.toFixed(2)}</p>
                  </div>
                  <div className="text-right">
                    <span className={`text-xs font-bold px-3 py-1 rounded-full ${
                      order.order_status === "shipped" ? "bg-blue-100 text-blue-700" :
                      order.order_status === "delivered" ? "bg-green-100 text-green-700" :
                      order.order_status === "processing" ? "bg-yellow-100 text-yellow-700" :
                      "bg-[#E6E2D3] text-[#5A5A5A]"
                    }`}>{order.order_status}</span>
                    {order.tracking_number && (
                      <p className="text-xs text-[#5A5A5A] mt-1">Track: {order.tracking_number}</p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
